/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode
} from "react";
import { useUserDetails } from "@/hooks/use-user-details";
import type { UserDetailsContext } from "@/types/core";
import { config } from "@/config";
import {
  getDefaultAuthenticatedRoute,
  getUserTypeBasedRedirect
} from "@/components";

// ============================================================================
// USER CONTEXT
// ============================================================================

const UserContext = createContext<UserDetailsContext | undefined>(undefined);

// ============================================================================
// USER CONTEXT PROVIDER
// ============================================================================

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const userDetails = useUserDetails();

  const getRedirectRoute = useCallback(() => {
    // If user details are still loading, return null to prevent premature navigation
    if (userDetails.isLoading) {
      if (config.debug.enabled) {
        console.log(
          "🔄 getRedirectRoute: User details still loading, waiting..."
        );
      }
      return null;
    }

    // If no user data, return null (not "/login" to avoid redirect loops)
    if (!userDetails.user) {
      if (config.debug.enabled) {
        console.log("🔄 getRedirectRoute: No user data available, waiting...");
      }
      return null;
    }

    // Unregistered users have no resolved role — the API returns "none".
    // Never send them to a role dashboard.
    if (userDetails.user.registrationRequired || !userDetails.userType) {
      if (config.debug.enabled) {
        console.log(
          "🔄 getRedirectRoute: Registration required, using default route"
        );
      }
      return getDefaultAuthenticatedRoute();
    }

    const userTypeRoute = getUserTypeBasedRedirect(userDetails.userType);
    if (userTypeRoute) {
      if (config.debug.enabled) {
        console.log(
          "✅ getRedirectRoute: Using user type route:",
          userTypeRoute,
          "for user type:",
          userDetails.userType
        );
      }
      return userTypeRoute;
    }

    // Fallback to default authenticated route
    if (config.debug.enabled) {
      console.log(
        "⚠️ getRedirectRoute: No user type route found, using default route"
      );
    }
    return getDefaultAuthenticatedRoute();
  }, [
    userDetails.user,
    userDetails.userType,
    userDetails.isLoading
  ]);

  // Memoize the context value to prevent unnecessary rerenders
  const userDetailsContext: UserDetailsContext = useMemo(
    () => ({
      ...userDetails,
      getRedirectRoute
    }),
    [userDetails, getRedirectRoute]
  );

  return (
    <UserContext.Provider value={userDetailsContext}>
      {children}
    </UserContext.Provider>
  );
}

// ============================================================================
// USER CONTEXT HOOK
// ============================================================================

export function useUserContext(): UserDetailsContext {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }

  return context;
}

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

/**
 * Hook to get the current user's full details from context
 */
export function useCurrentUserFromContext() {
  const { user } = useUserContext();
  return user;
}

/**
 * Hook to get the current user's ID from context
 */
export function useUserIdFromContext() {
  const { userId } = useUserContext();
  return userId;
}

/**
 * Hook to get the current user's UserType from context
 */
export function useUserTypeFromContext() {
  const { userType } = useUserContext();
  return userType;
}

/**
 * Hook to check if current user is a professional from context
 */
export function useIsProfessionalFromContext() {
  const { isProfessional } = useUserContext();
  return isProfessional;
}

/**
 * Hook to get a practice from context
 */
export function usePracticeFromContext() {
  const { user } = useUserContext();
  return user?.practice;
}

/**
 * Hook to check if current user is an admin from context
 */
export function useIsAdminFromContext() {
  const { isAdmin } = useUserContext();
  return isAdmin;
}

/**
 * Hook to check if current user is a support user from context
 */
export function useIsSupportFromContext() {
  const { isSupport } = useUserContext();
  return isSupport;
}

/**
 * Hook to check if current user is internal staff (admin or support) from context
 */
export function useIsStaffFromContext() {
  const { isStaff } = useUserContext();
  return isStaff;
}

/**
 * Hook to get a professional from context
 */
export function useProfessionalFromContext() {
  const { user } = useUserContext();
  return user?.professional;
}

/**
 * Hook to check if current user is a practice from context
 */
export function useIsPracticeFromContext() {
  const { isPractice } = useUserContext();
  return isPractice;
}

/**
 * Hook to get a verification status from context
 */
export function useVerificationStatusFromContext() {
  const { user } = useUserContext();

  const verificationStatus = useMemo(() => {
    if (user) {
      if (user.userType === "professional") {
        return user.professional?.verificationStatus ?? "pending";
      } else if (user.userType === "practice") {
        return user.practice?.verificationStatus ?? "pending";
      }
    }
  }, [user]);

  return verificationStatus;
}
